// Power-ups on screen (Godot scripts/pickups/powerup_pickup.gd + player.gd _apply_powerup_visual):
//   - DROPS: a tall faceted crystal standing on its point, self-lit in the power-up's colour,
//     spinning and bobbing over a soft ground glow, with a thin light shaft so you can find it
//     across the arena. Godot drew the same crystal with an unshaded tint, so it's built here.
//   - BUFFS on cowboys: a shell in the buff's colour that hugs the body (Metal Skin silver,
//     Overshield gold, the element's colour for Elemental Power), plus Magnetic Skin's orbiting
//     scrap ring, the same tell the Magnetic Brute wears. The stim is a rising pink shimmer.

import * as THREE from 'three';
import { PU, puColor } from '../../../sim/powerups';
import type { PickupNet, BuffNet } from '../../../sim/runwire';

const CRYSTAL_H = 1.1;
const MAX_AURAS = 4;

function glowTexture() {
  const c = document.createElement('canvas'); c.width = c.height = 64;
  const g = c.getContext('2d')!;
  const gr = g.createRadialGradient(32, 32, 0, 32, 32, 32);
  gr.addColorStop(0, 'rgba(255,255,255,1)'); gr.addColorStop(0.35, 'rgba(255,255,255,0.45)'); gr.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = gr; g.fillRect(0, 0, 64, 64);
  const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

const SHELL_VERT = /* glsl */ `
varying vec3 vN; varying vec3 vV; varying float vY;
void main() {
  vec4 wp = modelMatrix * vec4( position, 1.0 );
  vN = normalize( mat3( modelMatrix ) * normal ); vV = normalize( cameraPosition - wp.xyz ); vY = position.y;
  gl_Position = projectionMatrix * viewMatrix * wp;
}
`;
const SHELL_FRAG = /* glsl */ `
varying vec3 vN; varying vec3 vV; varying float vY;
uniform vec3 uCol; uniform float uTime; uniform float uOn; uniform float uKind;
void main() {
  float rim = pow( 1.0 - abs( dot( vN, vV ) ), 2.2 );
  float scan = 0.75 + 0.25 * sin( vY * 22.0 - uTime * 6.0 );
  // the stim shimmer rises; the skins hold still and glint
  float rise = uKind > 0.5 ? 0.5 + 0.5 * sin( vY * 9.0 - uTime * 9.0 ) : 1.0;
  float a = ( rim * 0.95 + 0.06 ) * scan * rise * uOn;
  gl_FragColor = vec4( uCol * a * 1.6, a );
}
`;

interface Drop { id: number; root: THREE.Group; crystal: THREE.Mesh; mat: THREE.MeshStandardMaterial; halo: THREE.Sprite; pool: THREE.Sprite; shaft: THREE.Mesh; seen: number; born: number }
interface Aura { root: THREE.Group; shell: THREE.Mesh; u: Record<string, THREE.IUniform>; ring: THREE.Group; ringMat: THREE.MeshStandardMaterial; over: THREE.Mesh; overU: Record<string, THREE.IUniform> }

export class PowerUpView {
  readonly group = new THREE.Group();
  private drops = new Map<number, Drop>();
  private auras: Aura[] = [];
  private crystalGeo: THREE.BufferGeometry;
  private shaftGeo: THREE.BufferGeometry;
  private shaftMat: THREE.MeshBasicMaterial;
  private glowTex: THREE.Texture;
  private frame = 0;
  private disposables: { dispose(): void }[] = [];

  constructor(scene: THREE.Scene, private time: { value: number }) {
    this.group.name = 'powerups';
    scene.add(this.group);
    // the crystal: an elongated octahedron, faceted (flat normals), standing on its point
    const g = new THREE.OctahedronGeometry(0.42, 0);
    g.scale(1, CRYSTAL_H / 0.84, 1);
    this.crystalGeo = g.index ? g.toNonIndexed() : g.clone(); this.crystalGeo.computeVertexNormals();
    g.dispose();
    this.shaftGeo = new THREE.CylinderGeometry(0.06, 0.18, 7, 8, 1, true);
    this.shaftGeo.translate(0, 3.5, 0);
    this.shaftMat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.22, blending: THREE.AdditiveBlending, depthWrite: false, toneMapped: false, side: THREE.DoubleSide });
    this.glowTex = glowTexture();
    this.disposables.push(this.crystalGeo, this.shaftGeo, this.shaftMat, this.glowTex);
    for (let i = 0; i < MAX_AURAS; i++) this.auras.push(this.makeAura());
  }

  private makeDrop(k: PickupNet): Drop {
    const col = new THREE.Color(puColor(k.type, k.el));
    const mat = new THREE.MeshStandardMaterial({ color: col.clone().multiplyScalar(0.6), emissive: col, emissiveIntensity: 1.8, roughness: 0.18, metalness: 0.1, flatShading: true });
    const crystal = new THREE.Mesh(this.crystalGeo, mat);
    crystal.castShadow = true;
    const halo = new THREE.Sprite(new THREE.SpriteMaterial({ map: this.glowTex, color: col, transparent: true, opacity: 0.85, blending: THREE.AdditiveBlending, depthWrite: false, toneMapped: false }));
    halo.scale.setScalar(2.2);
    const pool = new THREE.Sprite(new THREE.SpriteMaterial({ map: this.glowTex, color: col, transparent: true, opacity: 0.5, blending: THREE.AdditiveBlending, depthWrite: false, toneMapped: false }));
    pool.scale.set(3, 3, 1);
    const shaftMat = this.shaftMat.clone(); shaftMat.color.copy(col);
    const shaft = new THREE.Mesh(this.shaftGeo, shaftMat);
    const root = new THREE.Group();
    root.add(crystal, halo, pool, shaft);
    root.position.set(k.x, k.y, k.z);
    this.group.add(root);
    return { id: k.id, root, crystal, mat, halo, pool, shaft, seen: 0, born: this.time.value };
  }

  private makeAura(): Aura {
    const root = new THREE.Group();
    root.visible = false;
    const u = { uCol: { value: new THREE.Color() }, uTime: this.time, uOn: { value: 0 }, uKind: { value: 0 } };
    const sm = new THREE.ShaderMaterial({ vertexShader: SHELL_VERT, fragmentShader: SHELL_FRAG, uniforms: u, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending });
    const sg = new THREE.CapsuleGeometry(0.5, 1.0, 6, 16);
    sg.translate(0, 0.95, 0);
    const shell = new THREE.Mesh(sg, sm);
    const overU = { uCol: { value: new THREE.Color(1.0, 0.82, 0.3) }, uTime: this.time, uOn: { value: 0 }, uKind: { value: 0 } };
    const om = new THREE.ShaderMaterial({ vertexShader: SHELL_VERT, fragmentShader: SHELL_FRAG, uniforms: overU, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending });
    const og = new THREE.SphereGeometry(1.15, 20, 14);
    og.translate(0, 0.95, 0);
    const over = new THREE.Mesh(og, om);
    // Magnetic Skin's scrap ring: chunks orbiting the torso
    const ring = new THREE.Group();
    const ringMat = new THREE.MeshStandardMaterial({ color: 0x8a7aa8, emissive: new THREE.Color(0.55, 0.25, 0.9), emissiveIntensity: 0.9, metalness: 0.8, roughness: 0.35 });
    const chunk = new THREE.BoxGeometry(0.16, 0.1, 0.22);
    for (let i = 0; i < 8; i++) {
      const m = new THREE.Mesh(chunk, ringMat);
      const a = (i / 8) * Math.PI * 2;
      m.position.set(Math.cos(a) * 0.95, 1.05 + Math.sin(a * 3) * 0.12, Math.sin(a) * 0.95);
      m.rotation.set(a * 1.7, a, a * 0.6);
      ring.add(m);
    }
    root.add(shell, over, ring);
    this.group.add(root);
    this.disposables.push(sm, sg, om, og, ringMat, chunk);
    return { root, shell, u, ring, ringMat, over, overU };
  }

  /**
   * drops: the run block's pickups. buffs: running buffs. pos(pid): where that cowboy is drawn
   * (null if hidden or unknown).
   */
  update(drops: readonly PickupNet[], buffs: readonly BuffNet[], dt: number, pos: (pid: number) => { x: number; y: number; z: number } | null, cam?: THREE.Vector3) {
    this.frame++;
    const t = this.time.value;
    for (const k of drops) {
      let d = this.drops.get(k.id);
      if (!d) { d = this.makeDrop(k); this.drops.set(k.id, d); }
      d.seen = this.frame;
      const pop = Math.min(1, (t - d.born) / 0.5);
      const bob = Math.sin(t * 2.2 + k.id) * 0.12;
      d.crystal.position.y = 1.15 + bob;
      d.crystal.rotation.y = t * 1.6 + k.id;
      d.crystal.scale.setScalar(0.2 + 0.8 * (1 - (1 - pop) * (1 - pop)));
      d.halo.position.y = 1.15 + bob;
      d.pool.position.y = 0.08;
      (d.halo.material as THREE.SpriteMaterial).opacity = 0.65 + 0.2 * Math.sin(t * 4 + k.id);
      d.mat.emissiveIntensity = 1.6 + 0.5 * Math.sin(t * 4 + k.id);
      // up close it gets out of the camera's way: the shaft is for finding it across the arena,
      // and a crystal through the lens is a wall of colour
      const near = cam ? Math.hypot(cam.x - k.x, cam.z - k.z) : 99;
      const far = Math.min(1, Math.max(0, (near - 3) / 7));
      (d.shaft.material as THREE.MeshBasicMaterial).opacity = 0.22 * far;
      d.shaft.visible = far > 0.01;
      const close = Math.min(1, Math.max(0, (near - 2.0) / 2.5));
      d.crystal.visible = close > 0.05;
      (d.halo.material as THREE.SpriteMaterial).opacity *= close;
    }
    for (const d of [...this.drops.values()]) {
      if (d.seen === this.frame) continue;
      this.group.remove(d.root);
      d.mat.dispose(); (d.halo.material as THREE.Material).dispose(); (d.pool.material as THREE.Material).dispose(); (d.shaft.material as THREE.Material).dispose();
      this.drops.delete(d.id);
    }
    // auras
    let n = 0;
    for (const b of buffs) {
      if (n >= MAX_AURAS) break;
      const at = pos(b.pid);
      if (!at) continue;
      const a = this.auras[n++];
      a.root.visible = true;
      a.root.position.set(at.x, at.y, at.z);
      const on = b.type !== PU.None;
      a.shell.visible = on && b.type !== PU.MagneticSkin;
      a.ring.visible = b.type === PU.MagneticSkin;
      if (on) {
        a.u.uCol.value.setHex(puColor(b.type, b.el));
        // the last three seconds blink so you know it's going
        const ending = b.t <= 3 ? 0.55 + 0.45 * Math.sign(Math.sin(t * 14)) : 1;
        a.u.uOn.value = (b.type === PU.MetalSkin ? 1.1 : 0.8) * ending;
        a.u.uKind.value = b.type === PU.Stim ? 1 : 0;
        if (a.ring.visible) { a.ring.rotation.y = t * 3.2; a.ring.rotation.z = Math.sin(t * 1.3) * 0.18; a.ringMat.emissiveIntensity = 0.8 * ending; }
      }
      a.over.visible = b.over;
      a.overU.uOn.value = b.over ? 0.55 + 0.1 * Math.sin(t * 3) : 0;
    }
    for (let i = n; i < MAX_AURAS; i++) this.auras[i].root.visible = false;
    void dt;
  }

  clear() { this.update([], [], 0, () => null); }

  dispose() {
    this.clear();
    this.group.parent?.remove(this.group);
    for (const d of this.disposables) d.dispose();
  }
}
